/* istanbul ignore file */
import React, { useState } from 'react';
import {
  FormField,
  Input,
  Select,
  SpaceBetween,
  Button,
  Link,
  Box,
  Table,
  ColumnLayout,
  StatusIndicator,
  Modal,
  Container,
  Header,
} from '@cloudscape-design/components';
import { AuditTooltipMessages } from 'src/contstant/commonLabels';
import Tooltip from '../Tooltip';

export interface DocumentLinkItem {
  heading: string;
  url: string;
  title?: string;
}

interface DocumentLinkProps {
  links: DocumentLinkItem[];
  onChange: (links: DocumentLinkItem[]) => void;
}

const DocumentLink: React.FC<DocumentLinkProps> = ({ links = [], onChange }) => {
  const [newLink, setNewLink] = useState<DocumentLinkItem>({
    heading: '',
    url: '',
    title: '',
  });
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleAddLink = () => {
    if (newLink.url && newLink.heading) {
      // Only include title if it has a value
      const linkToAdd = {
        heading: newLink.heading,
        url: newLink.url,
        ...(newLink.title ? { title: newLink.title } : {}),
      };
      const updatedLinks = [...links, linkToAdd];
      onChange(updatedLinks);
      // Reset form
      setNewLink({
        heading: '',
        url: '',
        title: '',
      });

      setIsModalVisible(false);
    }
  };

  const handleRemoveLink = (index: number) => {
    const updatedLinks = [...links];
    updatedLinks.splice(index, 1);
    onChange(updatedLinks);
  };

  function isValidUrl(url: string) {
    try {
      new URL(url);
      return true;
    } catch (err) {
      return false;
    }
  }

  const isAddButtonDisabled =
    !newLink.url || !newLink.heading || !isValidUrl(newLink.url);

  return (
    <Container
      data-testid="document-link-container"
      header={
        <Header
          data-testid="document-link-header"
          variant="h3"
          actions={
            <Button
              data-testid="add-document-link-button"
              onClick={() => setIsModalVisible(true)}
            >
              Add Document Link
            </Button>
          }
          info={<Tooltip content={AuditTooltipMessages.DOCUMENTATION} />}
        >
          Standard Operating Procedures
        </Header>
      }
    >
      <Table
        data-testid="document-links-table"
        variant="embedded"
        columnDefinitions={[
          {
            id: 'document',
            header: 'Document',
            cell: (item) => item.heading,
          },
          {
            id: 'link',
            header: 'Link',
            maxWidth: 300,
            cell: (item) => (
              <Link data-testid="document-link" href={item.url}>
                {item.url}
              </Link>
            ),
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item: DocumentLinkItem) => {
              const index = links.findIndex(
                (link) => link.url === item.url && link.heading === item.heading,
              );
              return (
                <Button
                  data-testid="remove-link-button"
                  variant="link"
                  onClick={() => handleRemoveLink(index)}
                >
                  Remove
                </Button>
              );
            },
          },
        ]}
        items={links}
        empty={
          <Box
            data-testid="empty-state"
            margin={{ vertical: 'xs' }}
            textAlign="center"
            color="inherit"
          >
            <SpaceBetween size="m">
              <StatusIndicator data-testid="no-data-indicator" type="warning">
                No Data.
              </StatusIndicator>
            </SpaceBetween>
          </Box>
        }
        stripedRows
        stickyHeader
      />

      {/* Modal for adding links in popup mode */}

      <Modal
        data-testid="add-document-link-modal"
        visible={isModalVisible}
        onDismiss={() => setIsModalVisible(false)}
        header="Add Document Link"
        footer={
          <Box data-testid="modal-footer" float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                data-testid="cancel-button"
                variant="link"
                onClick={() => setIsModalVisible(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="add-link-button"
                variant="primary"
                onClick={handleAddLink}
                disabled={isAddButtonDisabled}
              >
                Add
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <FormField data-testid="document-title-field" label="Document Title">
            <Input
              data-testid="document-title-input"
              value={newLink.title || ''}
              onChange={({ detail }) =>
                setNewLink({ ...newLink, title: detail.value, heading: detail.value })
              }
              placeholder="Enter document title"
            />
          </FormField>

          <FormField
            data-testid="url-field"
            label="URL"
            errorText={
              newLink.url && !isValidUrl(newLink.url)
                ? 'Please enter a valid URL'
                : undefined
            }
          >
            <Input
              data-testid="url-input"
              value={newLink.url}
              onChange={({ detail }) => setNewLink({ ...newLink, url: detail.value })}
              placeholder="https://example.com"
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </Container>
  );
};

export default DocumentLink;
